import { FC, useState } from 'react'
import Image from 'next/image'

import {
  ellipseSteps,
  personPhoneYellow,
  personOverlay,
  checkIcon,
  currencyExchangeIcon,
} from '@ui/assets'

interface Step {
  title: string
  description: string
}

const steps: Step[] = [
  {
    title: 'Apply in Minutes',
    description:
      'See how much of an advance you qualify for with no credit check, just by linking the bank account where you receive income.',
  },
  {
    title: 'Request Advance',
    description:
      'See how much of an advance you qualify for with no credit check, just by linking the bank account where you receive income.',
  },
  {
    title: 'Cash Out & Spend',
    description:
      'See how much of an advance you qualify for with no credit check, just by linking the bank account where you receive income.',
  },
  {
    title: 'Repay & Request',
    description:
      'See how much of an advance you qualify for with no credit check, just by linking the bank account where you receive income.',
  },
]

export const HowToStart: FC = () => {
  const [selectedStep, setSelectedStep] = useState<number>(0)

  return (
    <section className="bg-white py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2 className="font-display mb-20 text-center text-4xl leading-none tracking-[-1.52px] text-[#0a2d4a] md:text-6xl lg:text-[56px]">
          How to start with
          <br />
          <span className="text-[#29bfed]">ATM Instant Cash</span>
        </h2>

        <div className="flex flex-col items-start gap-8 lg:flex-row lg:justify-between lg:gap-16">
          {/* Left side - Phone mockup */}
          <div className="relative h-auto w-full flex-shrink-0 overflow-hidden rounded-[20px] bg-[#0a3754] sm:h-[500px] lg:h-[720px] lg:w-[621px]">
            {/* Ellipse background */}
            <div className="absolute left-[-470px] top-[-530px] hidden lg:block">
              <div className="rotate-[57.2deg]">
                <Image src={ellipseSteps} width={696} height={911} alt="" />
              </div>
            </div>

            {/* Person photo background */}
            <div className="absolute left-[-556px] top-[-299px] hidden h-[1155px] w-[1732px] lg:block">
              <Image
                src={personPhoneYellow}
                width={1732}
                height={1155}
                alt=""
                className="h-full w-full object-cover"
              />
            </div>

            {/* Overlay image */}
            <div className="absolute left-[-185px] top-[-5px] hidden h-[730px] w-[1095px] lg:block">
              <Image
                src={personOverlay}
                width={1095}
                height={730}
                alt=""
                className="h-full w-full object-cover"
              />
            </div>

            {/* Bottom cards */}
            <div className="absolute bottom-6 left-6 flex w-[376px] flex-col gap-2">
              {/* Advance Processing card */}
              <div className="flex flex-col gap-[74px] rounded-[12px] bg-white p-6">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-[10px]">
                    <div className="flex h-[22px] w-[22px] items-center justify-center rounded-[11px] bg-[#34af46] p-[2px]">
                      <Image src={checkIcon} width={16} height={16} alt="" />
                    </div>
                    <p className="font-display text-[26px] leading-[0.8] tracking-[-0.52px] text-[#0a3754]">
                      Advance Processing
                    </p>
                  </div>
                  <p className="text-[18px] tracking-[-0.36px] text-[#0a3754]/60">
                    Auto-matched
                  </p>
                </div>
                {/* Progress bar */}
                <div className="relative h-[8px] w-full rounded-full bg-[#e6ebee]">
                  <div className="absolute h-[8px] w-[42%] rounded-full bg-[#006fff]" />
                </div>
              </div>

              {/* Money Deposited card */}
              <div className="flex items-center justify-between rounded-[12px] bg-white px-2 py-2">
                <div className="flex items-center gap-4">
                  <div className="flex h-[68px] w-[68px] items-center justify-center rounded-[8px] bg-[#34af46]/15 p-[18px]">
                    <Image
                      src={currencyExchangeIcon}
                      width={31}
                      height={31}
                      alt=""
                    />
                  </div>
                  <p className="text-[18px] tracking-[-0.36px] text-[#1c1b17]">
                    Money Deposited
                  </p>
                </div>
                <p className="font-display pr-5 text-[18.5px] tracking-[-0.37px] text-[#0a3754]">
                  $100.00
                </p>
              </div>
            </div>
          </div>

          {/* Right side - Steps */}
          <div className="flex w-full flex-col lg:w-[460px]">
            {steps.map((step, index) => (
              <button
                key={`${step.title}-${index}`}
                onClick={() => setSelectedStep(index)}
                className={`rounded-[12px] px-5 py-6 text-left transition-all ${
                  selectedStep === index
                    ? 'bg-[#e5f5fc] opacity-100'
                    : 'opacity-50 hover:opacity-75'
                }`}
              >
                <div
                  className={`pl-5 ${selectedStep === index ? 'border-l-4 border-[#29bfed]' : 'border-l-0 border-transparent'}`}
                >
                  <h3 className="font-display mb-6 text-2xl leading-none tracking-[-0.64px] text-[#0a2d4a] md:text-3xl lg:text-[32px]">
                    {step.title}
                  </h3>
                  <p className="text-[19px] leading-[1.3] text-[#0a2d4a]">
                    {step.description}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
